import BubbleText from "@/components/BubbleText"
import { t } from "i18next"
import Card from "@/components/card"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useEffect, useState, useRef } from "react"
import Space from "@/components/space"
import ListItem from "@/components/list-item"
import Button from "@/components/button"
import SaveToast from "@/components/SaveToast"
import { faCheckCircle, faTriangleExclamation, faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { useNavigate, useLocation } from "react-router-dom"
import unitStore from "../../unit/model/unit.store"
import SearchBar from "@/components/search-bar"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Unit } from "../../unit/model/unit.types"
import { Risk } from "../model/risk.types"
import RiskTypeChip from "./RiskTypeChip"
import RiskListItemDetail from "./RiskListItemDetail"

interface Props {
  associatedRisks: Risk[]
  riskType?: "phy" | "rps"
}

export function RisksAddFromModel(props: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const unit = useAppSelector(unitStore.selectors.active)
  const units = useAppSelector(unitStore.selectors.list)
  const risks = useAppSelector(riskStore.selectors.list)
  const selectedRisks = useAppSelector(riskStore.selectors.listSelected)
  const status = useAppSelector(riskStore.selectors.status)

  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState<string>("")
  const unitRefsMap = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    dispatch(riskStore.thunks.fetchValidatedAndSet(null))
  }, [])

  // Scroll to selected unit
  useEffect(() => {
    const selectedUnitId = Array.from(selectedUnits)[0]
    if (selectedUnitId && unitRefsMap.current.has(selectedUnitId)) {
      const element = unitRefsMap.current.get(selectedUnitId)
      element?.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [selectedUnits])

  const filteredUnits = units
    .filter((u) => u.name.toLowerCase().includes(search.toLowerCase().trim()) && u.id !== unit.id)
    .filter((u) => risks.some((r) => r.UnitId === u.id && (props.riskType ? r.rps === (props.riskType === "rps") : true)))

  const filteredRisks = risks.filter((r) => selectedUnits.has(r.UnitId ?? ""))

  const handleSelect = (riskId: string) => {
    // Si le risque est déjà sélectionné, le désélectionner
    if (selectedRisks.some((r) => r.id === riskId)) {
      dispatch(riskStore.actions.clearSelected())
    } else {
      // Sinon, déselectionner tous les autres et sélectionner celui-ci
      dispatch(riskStore.actions.clearSelected())
      dispatch(riskStore.actions.select(riskId))
    }
  }

  const handleSave = async () => {
    if (!unit) return

    const { backTo } = (location.state as any) || {}

    try {
      const risk = selectedRisks[0]
      const createdRisk = await dispatch(
        riskStore.thunks.createEntity({
          ...risk,
          UnitId: unit.id,
          rps: props.riskType === "rps",
          associatedRisks: props.associatedRisks
        })
      ).unwrap()

      if (createdRisk?.id) {
        navigate(`/risks/risk/${createdRisk.id}`, { state: { askSave: true, backTo } })
      } else {
        navigate(backTo || -1)
      }
    } catch (err) {
      console.error("❌ Erreur lors de la sauvegarde des risques :", err)
    }
  }

  return (
    <div className="h-full mt-24">
      <div className="flex justify-between">
        <Space />
        <div className="flex -mt-32">
          <div className="max-w-[500px] h-[100px] mr-8 mt-auto mb-4 text-2xl">
            <BubbleText text={t(`Sélectionnez une unité pour modèle`)} orientation="right" />
          </div>
          <img
            src={require("@/assets/assistant/owl_female.png")}
            alt="owl assistant"
            className="object-cover object-top scale-x-[-1]"
            style={{ width: 200, height: 180 }}
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <h2>{selectedUnits.size === 0 ? t(`Liste des unités modèles`) : t(`Sélection des « Risques »`)}</h2>

        <div>
          {/* 🔍 SearchBar en haut de la liste */}
          <div className="my-4">
            <SearchBar onSearch={(val) => setSearch(val)} />
          </div>

          {/* Liste filtrée */}
          <div>
            {filteredUnits.length > 0 ? (
              filteredUnits.map((unit: Unit) => {
                const unitRisks = risks.filter((r) => r.UnitId === unit.id)
                const rps = unitRisks
                  .filter((r) => r.rps)
                  .sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))
                const phy = unitRisks
                  .filter((r) => !r.rps)
                  .sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))
                const rpsCount = rps.length
                const phyCount = phy.length
                const rpsColor = (rps.length > 0 ? rps[0].valueEffectiveColor : "#ccc") || undefined
                const phyColor = (phy.length > 0 ? phy[0].valueEffectiveColor : "#ccc") || undefined

                return (
                  <div
                    key={unit.id}
                    ref={(el) => {
                      if (el) unitRefsMap.current.set(unit.id, el)
                    }}>
                    <ListItem
                      isSmall
                      isEditable
                      isActive={selectedUnits.has(unit.id)}
                      onClick={() => {
                        // Only allow one unit open at a time
                        if (selectedUnits.has(unit.id)) {
                          setSelectedUnits(new Set())
                        } else {
                          setSelectedUnits(new Set([unit.id]))
                        }
                      }}
                      className="flex justify-between items-center">
                      <span className="font-bold">{unit.name}</span>
                      <div className="flex items-center gap-4">
                        <RiskTypeChip
                          isRps={false}
                          count={phyCount}
                          showCount
                          color={phyColor}
                          isSmall
                          showLabel={true}
                        />
                        <RiskTypeChip
                          isRps={true}
                          count={rpsCount}
                          showCount
                          color={rpsColor}
                          isSmall
                          showLabel={true}
                        />
                        <div className="relative ml-2">
                          <div className="w-6 h-6 flex items-center justify-center rounded-[50%] grey">
                            <FontAwesomeIcon
                              icon={faTriangleExclamation}
                              style={{
                                fontSize: "12px",
                                color: "white"
                              }}
                            />
                          </div>
                          <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full grey text-white flex items-center justify-center text-sm font-bold">
                            {rpsCount + phyCount}
                          </div>
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className="text-gray-400 text-sm transition-transform"
                          style={{
                            transform: selectedUnits.has(unit.id) ? "rotate(0deg)" : "rotate(-90deg)"
                          }}
                        />
                      </div>
                    </ListItem>

                    {selectedUnits.has(unit.id) && unitRisks.length > 0 && (
                      <div className="ml-8">
                        {filteredRisks.map((risk) => {
                          const isActive = selectedRisks.some((r) => r.id === risk.id)
                          return (
                            <RiskListItemDetail
                              key={risk.id}
                              risk={risk}
                              onSelect={handleSelect}
                              isSelected={isActive}
                              showCheckbox={true}
                              hideFirstAttribute={false}
                            />
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <p className="text-gray-400 italic px-2">{t("Aucune unité trouvée")}</p>
            )}
          </div>
        </div>
      </Card>

      {selectedRisks.length > 0 && (
        <SaveToast status={status}>
          <Button
            disabled={selectedRisks.length === 0}
            className="primary"
            icon={faCheckCircle}
            onClick={handleSave}
            isFullWidth
            isWithBorder>
            Dupliquer
          </Button>
        </SaveToast>
      )}
    </div>
  )
}
